import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import NextArrow from '@mui/icons-material/ChevronRight';
import BackArrow from '@mui/icons-material/ChevronLeft';
import { UnreadBadge } from '@xolv-mybrightlink/common';
import { useUnreadCount } from '../hooks/store/useUnreadCount';
import { usePatient, usePatientGuids } from '../hooks/store/usePatientHooks';

const PatientChip = (props: {
  patientGuid: string;
  selected?: boolean;
  onSelect: () => void;
}) => {
  const patient = usePatient(props.patientGuid);
  const unreadCount = useUnreadCount({
    type: 'patient',
    guid: props.patientGuid,
  });
  return (
    <Box
      flex={1}
      display='inline-flex'
      alignItems='center'
      justifyContent='center'
      sx={{
        cursor: 'pointer',
        padding: '10px',
        width: '100%',
        minWidth: '50px',
      }}
      {...(props.selected && {
        borderRadius: 22,
        boxShadow:
          '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
        backgroundColor: 'white',
      })}
      onClick={props.onSelect}
    >
      <UnreadBadge count={unreadCount}>
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: props.selected ? 600 : 'normal',
          }}
        >
          {patient?.firstName}
        </Typography>
      </UnreadBadge>
    </Box>
  );
};

type PatientListProps = {
  defaultPatientGuid?: string;
  onSelectPatient: (patientGuid: string) => void;
};

const MAX_PATIENTS = 3;
const arrowStyle: SxProps<Theme> = {
  width: 40,
  height: 40,
  position: 'absolute',
  border: 'solid 0.5px #d8dade',
  color: '#898f9b',
  bgcolor: 'white',
  boxShadow:
    '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
  '&.MuiIconButton-root': {
    '&:hover': {
      backgroundColor: 'white',
    },
  },
};

export const PatientList = (props: PatientListProps) => {
  const { defaultPatientGuid, onSelectPatient } = props;
  const patientGuids = usePatientGuids();
  const [index, setIndex] = useState(0);
  const maxIndex = Math.max(0, patientGuids.length - MAX_PATIENTS);
  const patientGuidsToDisplay = patientGuids.slice(index, index + MAX_PATIENTS);

  // This need to be run one when switching message mode (expanded vs collapsed)
  useEffect(() => {
    if (
      defaultPatientGuid &&
      !patientGuidsToDisplay.includes(defaultPatientGuid)
    ) {
      const patientIndex = patientGuids.findIndex(
        (guid) => guid === defaultPatientGuid,
      );
      if (patientIndex >= 0) {
        setIndex(Math.max(0, patientIndex - MAX_PATIENTS + 1));
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (patientGuids.length <= 1) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {index > 0 && (
        <IconButton
          onClick={() => setIndex(Math.max(0, index - 1))}
          sx={{ ...arrowStyle }}
        >
          <BackArrow fontSize='large' />
        </IconButton>
      )}
      <Box
        sx={{
          bgcolor: '#f7f8fb',
          borderRadius: 8,
          padding: 2.5,
          height: 64,
          display: 'flex',
          mx: 5,
          alignItems: 'center',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {patientGuidsToDisplay.map((patientGuid) => (
          <PatientChip
            key={patientGuid}
            patientGuid={patientGuid}
            selected={patientGuid === defaultPatientGuid}
            onSelect={() => onSelectPatient(patientGuid)}
          />
        ))}
      </Box>
      {index < maxIndex && (
        <IconButton
          onClick={() => setIndex(Math.min(maxIndex, index + 1))}
          sx={{ ...arrowStyle, right: 0 }}
        >
          <NextArrow fontSize='large' />
        </IconButton>
      )}
    </Box>
  );
};
